/**
 * Cemptystate Component
 * Empty state display with neutral triangle and splatter images
 */
"use client";
import type { CSSProperties } from "react";
import { motion } from "framer-motion";
import { Ctext } from "../Ctext";
import { Cimage } from "../Cimage";
import { TextPreset } from "@/constants/componentPresets";
import { FontWeight } from "@/theme/typography";
import { dataEl, type DataElScope } from "@/utils/dataEl";
/** The line the splatter carries when the caller does not override it. */
const DEFAULT_EMPTY_TEXT = "Nothing Yet";
/** Sized to sit inside the 270px splatter at the default two-word line. */
const DEFAULT_EMPTY_TEXT_SIZE = 32;
export interface CemptystateProps {
  /** The component rendering this empty state, e.g. "Csessionlist/EmptyState". Required: it is
   *  what makes an inspected node say WHICH surface it belongs to. */
  dataElScope: DataElScope;
  className?: string;
  style?: CSSProperties;
  /** Overrides the line on the splatter. Callers on a search surface say what
   *  was not found; the default speaks about a collection that is still empty.
   *
   *  `null` is the third case and it is not the same as an empty string: it
   *  removes the caption NODE, so the mark is the triangle and the splatter
   *  swipe alone. An absent content image is the ruled use — the state is
   *  already saying "there is nothing here" in paint, and a caption under it
   *  says it a second time in words. `undefined` keeps the default line, which
   *  is what every caller that passes nothing gets. */
  text?: string | null;
  /** The line is `nowrap` inside a fixed-width splatter, so a longer override
   *  needs a smaller size to stay on it. */
  textSize?: number;
}
export function Cemptystate({
  dataElScope,
  className,
  style,
  text = DEFAULT_EMPTY_TEXT,
  textSize = DEFAULT_EMPTY_TEXT_SIZE,
}: CemptystateProps) {
  return (
    <motion.div
      data-el={dataEl(dataElScope, 'Root')}
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
      style={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: "350px",
        alignItems: "center",
        justifyContent: "center",
        gap: "5px",
        ...style,
      }}
    >
      {/* Neutral empty triangle */}
      <div
        data-el={dataEl(dataElScope, 'Triangle')}
        style={{ display: "flex" }}
      >
        <Cimage
          src="/triangles/neutral-empty.png"
          alt=""
          width={120}
          height={90}
          static={true}
          objectFit="contain"
          style={{
            width: "120px",
            height: "90px",
            translate: "-4px",
          }}
        />
      </div>

      {/* Neutral splatter with text overlay */}
      <motion.div
        data-el={dataEl(dataElScope, 'Splatter')}
        initial={{ clipPath: "inset(0 100% 0 0)" }}
        animate={{ clipPath: "inset(0 0% 0 0)" }}
        transition={{ duration: 0.6, ease: "easeInOut", delay: 0.2 }}
        style={{ display: "flex", position: "relative" }}
      >
        <Cimage
          src="/triangles/neutral-splatter.png"
          alt=""
          width={270}
          height={90}
          static={true}
          objectFit="contain"
          style={{
            width: "270px",
            height: "90px",
            translate: "9px -6px",
          }}
        />

        {/* The line, centred on the splatter. Absent — not empty — when the
            caller passed `null`: the copy is absolutely positioned, so dropping
            the node leaves the splatter's own box exactly as it was. */}
        {text !== null && (
          <div
            data-el={dataEl(dataElScope, 'Caption')}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              translate: "-2px 0px",
            }}
          >
            <Ctext
              preset={TextPreset.Mild}
              size={textSize}
              opacity={0.56}
              weight={FontWeight.Light}
              style={{ whiteSpace: "nowrap" }}
            >
              {text}
            </Ctext>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
